import React, { useEffect, useState } from "react";
import { Route, useLocation, useNavigate } from "react-router";
import { Routes } from "react-router";
import {
  EuiBreadcrumb,
  EuiEmptyPrompt,
  EuiLoadingChart,
  EuiLoadingSpinner,
} from "@elastic/eui";
import {
  AdminHomePage,
  AnketaActionsEnum,
  AnketaResourcesEnum,
  hasPermission,
  Home,
  useApi,
} from "../..";
import { useAnketaI18n } from "../../internationalization/context/anketaI18n";
import { AnketaCatalogTable } from "../../questionnaires/page/catalogTable";
import {
  AnketaQuereinarieHome,
  AnketaQuestionnaireTable,
  AnketaRender,
} from "../../questionnaires/page";
import { AnketaContextProvider } from "../../questionnaires/components";
import { QuestionnaireSaveProvider } from "../../questionnaires/save";
import { Catalog } from "anketa-core/types";
import { AnketaCatalogs } from "../../questionnaires";
import { useAnketaAuth } from "../../auth/context";
import {
  getOpsanioFeaturePermission,
  getOpsanioPermissions,
  OpsanioHomePage,
  OpsanioVersionOverview,
  OpsanioWaveVersionManager,
} from "src/opsanio";
import { OpsanioFeatureEnum } from "../..";
import AutonubilHomepage from "src/homepage";
import { AutonubilImprint } from "src/homepage/imprint";
import { AutonubilSecurity } from "src/homepage/security";
import { AutonubilContactForm } from "src/homepage/contactForm";
import { ContactPage } from "src/opsanio/contact";
import { OpsanioApplianceDashboard } from "src/opsanio/appliance";
import { OpsanioHealthWall } from "src/opsanio/health";
import { OpsanioOplOverviewPage, OpsanioOplPage } from "src/opsanio/opl";
import { NotFoundPage } from "src/pages/status";
import { OpsanioHardwarePage } from "src/opsanio/hardware";

export const Navigation = (): JSX.Element => {
  const location = useLocation();
  const navigate = useNavigate();
  const { translate, currentLang } = useAnketaI18n();
  const { keycloak, user, permissionMatrix } = useAnketaAuth();
  const { Get } = useApi();
  const [navigationBreadcrumbs, setNavigationBreadcrumbs] = useState<
    EuiBreadcrumb[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);

  const [catalogs, setCatalogs] = useState<Array<AnketaCatalogs>>([]);

  const [catalog, setCatalog] = useState<Catalog>(undefined);
  const [retrigger, setRetrigger] = useState<number>(0);

  const generateBreadcrumbs = async (): Promise<EuiBreadcrumb[]> => {
    const pathItems = location.pathname.split("/").filter(Boolean);
    const rootItem = pathItems[0];
    const breadcrumbs: EuiBreadcrumb[] = [];

    for (const [idx, item] of pathItems.entries()) {
      let cumulativePath = `/${pathItems.slice(0, idx + 1).join("/")}`;

      let text = translate(`anketa.${item}`);

      if (idx >= 1) {
        switch (rootItem) {
          case AnketaResourcesEnum.questionnaires: {
            const catalog = catalogs.find((q) => q.id === item)?.displayName;
            const questionnaire = catalogs
              .find((c) => c.questionnaire.find((q) => q.id === item))
              ?.questionnaire.find((q) => q.id === item)?.displayName;

            text = catalog ?? questionnaire ?? item;
            break;
          }
        }
      }

      breadcrumbs.push({
        text,
        onClick: () => navigate(cumulativePath),
      });
    }

    return breadcrumbs;
  };

  const updateCatalog = (c: Catalog | undefined) => {
    setCatalog(c);
  };

  const questionnairePermission = hasPermission(
    user,
    AnketaResourcesEnum.questionnaires,
    AnketaActionsEnum.view,
    undefined,
    permissionMatrix,
  );

  useEffect(() => {
    if (
      questionnairePermission &&
      location.pathname.startsWith(`/${AnketaResourcesEnum.questionnaires}`)
    ) {
      setLoading(true);
      Get<Array<AnketaCatalogs>>({
        path: `v2/${AnketaResourcesEnum.questionnaires}/catalogs`,
        responseType: "json",
        token: keycloak.token,
      })
        .then((response) => {
          if (response.data !== null) {
            setCatalogs(response.data);
          }
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
          console.error(err);
        });
    }
  }, [questionnairePermission, retrigger, location]);

  useEffect(() => {
    generateBreadcrumbs().then((crumb) => {
      setNavigationBreadcrumbs(crumb);
    });
  }, [location, currentLang, catalogs]);

  return (
    <AnketaContextProvider locale={currentLang} catalog={catalog}>
      <QuestionnaireSaveProvider>
      <Routes>
        <Route
          path="/"
          element={
            <Home
              breadcrumbs={navigationBreadcrumbs}
              updateCatalog={updateCatalog}
            />
          }
        >
          <Route path="/home" element={<AutonubilHomepage />} />
          <Route path="/home/imprint" element={<AutonubilImprint />} />
          {/* Vulnerability-disclosure policy. One language per URL (work item
              opsanio/documentation#333) - security.txt points at /security, so
              these paths are published and must not move. Under the same layout
              route as the imprint, so both get the header and the footer. */}
          <Route path="/security" element={<AutonubilSecurity lang="de" />} />
          <Route path="/security/de" element={<AutonubilSecurity lang="de" />} />
          <Route path="/security/en" element={<AutonubilSecurity lang="en" />} />
          <Route path="/home/contact" element={<AutonubilContactForm />} />
          {questionnairePermission && (
            <Route
              path={`/${AnketaResourcesEnum.questionnaires}`}
              element={<AnketaQuereinarieHome />}
            >
              <Route
                path={`/${AnketaResourcesEnum.questionnaires}`}
                element={
                  loading ? (
                    <EuiEmptyPrompt
                      icon={<EuiLoadingSpinner size="xl" />}
                      title={<h2>Loading</h2>}
                    />
                  ) : (
                    <AnketaCatalogTable catalogs={catalogs} />
                  )
                }
              />
              <Route
                path={`/${AnketaResourcesEnum.questionnaires}/:catalogId`}
                element={
                  loading ? (
                    <EuiEmptyPrompt
                      icon={<EuiLoadingSpinner size="xl" />}
                      title={<h2>Loading</h2>}
                    />
                  ) : (
                    <AnketaQuestionnaireTable
                      catalogs={catalogs}
                      setRetrigger={setRetrigger}
                    />
                  )
                }
              />
              <Route
                path={`/${AnketaResourcesEnum.questionnaires}/:catalogId/:questionnaireId`}
                element={
                  <AnketaRender
                    key={"questionnaireId-page"}
                    updateCatalog={updateCatalog}
                  />
                }
              />
            </Route>
          )}
          {/* Unknown (or not permitted) paths: a 404 inside the layout. */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        {user?.isSudo && (
          <Route
            path="/admin"
            element={<AdminHomePage breadcrumbs={navigationBreadcrumbs} />}
          />
        )}
        {getOpsanioPermissions(user, permissionMatrix) && (
          <Route
            path={`/${AnketaResourcesEnum.opsanio}`}
            element={<OpsanioHomePage breadcrumbs={navigationBreadcrumbs} />}
          >
            {getOpsanioFeaturePermission(
              user,
              OpsanioFeatureEnum.updates,
              permissionMatrix,
            ) && (
              <Route
                path={`/${AnketaResourcesEnum.opsanio}/update`}
                element={<OpsanioWaveVersionManager />}
              />
            )}
            {/*
              The version overview is the read-only, release-side view of the
              same concern as the wave update manager, so it shares the
              `updates` feature gate rather than introducing a matrix cell that
              would fail closed until an admin saves the permission matrix.
            */}
            {getOpsanioFeaturePermission(
              user,
              OpsanioFeatureEnum.updates,
              permissionMatrix,
            ) && (
              <Route
                path={`/${AnketaResourcesEnum.opsanio}/version`}
                element={<OpsanioVersionOverview />}
              />
            )}
            {/*
              The OPL board is per appliance and reached from the appliance
              flyout; `/opl` is the cross-appliance overview of open points
              (side nav "Offene Punkte"). Gated on `opsanio/opl · view`; the
              routes remain the boundary - the overview's API filters to the
              appliances the caller may read.
            */}
            {hasPermission(
              user,
              AnketaResourcesEnum.opsanioOpl,
              AnketaActionsEnum.view,
              undefined,
              permissionMatrix,
            ) && (
              <>
                <Route
                  path={`/${AnketaResourcesEnum.opsanio}/opl`}
                  element={<OpsanioOplOverviewPage />}
                />
                <Route
                  path={`/${AnketaResourcesEnum.opsanio}/opl/:appliance`}
                  element={<OpsanioOplPage />}
                />
              </>
            )}
            {getOpsanioFeaturePermission(
              user,
              OpsanioFeatureEnum.contact,
              permissionMatrix,
            ) && (
              <Route
                path={`/${AnketaResourcesEnum.opsanio}/contact`}
                element={<ContactPage updateCatalog={updateCatalog} />}
              />
            )}
            {getOpsanioFeaturePermission(
              user,
              OpsanioFeatureEnum.appliance,
              permissionMatrix,
            ) && (
              <Route
                path={`/${AnketaResourcesEnum.opsanio}/appliance`}
                element={
                  <OpsanioApplianceDashboard updateCatalog={updateCatalog} />
                }
              />
            )}
            {/*
              The health wall reads the same endpoints as the appliance page
              (summary + PRTG status), so it shares the `appliance` feature
              gate instead of a new matrix cell that would fail closed until an
              admin saves the permission matrix. `?kiosk=1` hides the chrome.
            */}
            {getOpsanioFeaturePermission(
              user,
              OpsanioFeatureEnum.appliance,
              permissionMatrix,
            ) && (
              <Route
                path={`/${AnketaResourcesEnum.opsanio}/health`}
                element={<OpsanioHealthWall />}
              />
            )}
            {getOpsanioFeaturePermission(
              user,
              OpsanioFeatureEnum.hardware,
              permissionMatrix,
            ) && (
              <Route
                path={`/${AnketaResourcesEnum.opsanio}/hardware`}
                element={<OpsanioHardwarePage />}
              />
            )}
            <Route
              path={`/${AnketaResourcesEnum.opsanio}/*`}
              element={<NotFoundPage />}
            />
          </Route>
        )}
        {/*         {getMedocinoSupportPermissions(user) && (
          <Route
            path={`/${AnketaResourcesEnum.medocinoSupport}`}
            element={<OnboardingApp breadcrumbs={navigationBreadcrumbs} />}
          />
        )} */}
      </Routes>
      </QuestionnaireSaveProvider>
    </AnketaContextProvider>
  );
};
